import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MeasurementsTable from '../components/MeasurementsTable';
import StatsGrid from '../components/StatsGrid';
import Pagination from '../components/Pagination';
import { User } from '../data/mockData';
import {
  getMeasurements,
  Measurement,
  MeasurementStatusCounts
} from '../services/measurementService';
import {
  MEASUREMENT_APP_EVENT,
  MEASUREMENT_CREATION_EVENT_TYPE,
} from '../constants/measurementEvents';
import './MeasurementsPage.css';

const PAGE_SIZE_DESKTOP = 20;
const PAGE_SIZE_MOBILE = 10;

const emptyStatusKpis: MeasurementStatusCounts = {
  upcoming: 0,
  active: 0,
  reported: 0,
  failed: 0
};

const MeasurementsPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [statusKpis, setStatusKpis] = useState<MeasurementStatusCounts>(emptyStatusKpis);
  const navigate = useNavigate();

  const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const loadMeasurements = useCallback(async (page: number) => {
    try {
      setIsLoading(true);
      const result = await getMeasurements(page, pageSize);
      setMeasurements(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setStatusKpis(result.statusCounts ?? emptyStatusKpis);
    } catch (err) {
      setError('Failed to load measurements');
    } finally {
      setIsLoading(false);
    }
  }, [pageSize]);

  const refreshCurrentPageSilently = useCallback(async () => {
    try {
      const result = await getMeasurements(currentPage, pageSize);
      setMeasurements(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setStatusKpis(result.statusCounts ?? emptyStatusKpis);
    } catch {
      /* keep existing list */
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadMeasurements(currentPage);
  }, [currentPage, loadMeasurements]);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const d = (e as CustomEvent<{ type?: string; status?: string }>).detail;
      if (d?.type !== MEASUREMENT_CREATION_EVENT_TYPE) {
        return;
      }
      const st = (d.status || '').toLowerCase();
      if (st === 'reported' || st === 'failed' || st === 'upcoming') {
        void refreshCurrentPageSilently();
      }
    };
    window.addEventListener(MEASUREMENT_APP_EVENT, onEvent);
    return () => window.removeEventListener(MEASUREMENT_APP_EVENT, onEvent);
  }, [refreshCurrentPageSilently]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const handleRowClick = (measurementId: string) => {
    navigate(`/measurements/${measurementId}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Layout user={user}>
      <div className="measurements-page">
        <div className="page-header">
          <h1>Measurements</h1>
          <button
            className="create-button"
            onClick={() => navigate('/create-measurement')}
          >
            Create New Measurement
          </button>
        </div>

        {isLoading ? (
          <div className="loading-state">Loading measurements...</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <>
            <StatsGrid statusCounts={statusKpis} />

            {totalCount === 0 ? (
              <div className="empty-state">
                <h3>No measurements yet</h3>
                <p>Create your first measurement to get started.</p>
                <button
                  className="create-button"
                  onClick={() => navigate('/create-measurement')}
                >
                  Create Measurement
                </button>
              </div>
            ) : (
              <>
                <MeasurementsTable
                  measurements={measurements}
                  onRowClick={handleRowClick}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default MeasurementsPage;
